import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  AnimatePresence,
  motion,
  useScroll,
  useTransform,
} from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { alaViewport } from './alaMotion';
import { CountUp } from './CountUp';
import { plainText } from './propertyUtils';

const MARQUEE_IMAGES = [
  '',
  '',
  '',
  '',
  '',
];

const ETHOS_IMAGES = [
  '',
  '',
  '',
  '',
  '',
  '',
  '',
];

const JOURNEY_IMAGES = [
  '',
  '',
  '',
  '',
  '',
];

const IMPACT_HERO_IMAGE =
  '';

const GIVING_IMAGES = [
  '',
  '',
  '',
];

const PLACE_WORDS = ['people', 'make', 'the', 'place'];

const wordVariants = {
  hidden: { opacity: 0, y: 60, rotate: -6, scale: 0.7 },
  visible: {
    opacity: 1,
    y: 0,
    rotate: 0,
    scale: 1,
    transition: { type: 'spring' as const, stiffness: 260, damping: 14 },
  },
};

const primaryCtaClass =
  'group inline-flex w-fit items-center justify-center gap-2 rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-4 font-heading text-sm font-medium text-[rgb(var(--ala-on-secondary))] transition-colors hover:bg-[#4080d0]';

const secondaryCtaClass =
  'group inline-flex w-fit items-center justify-center gap-2 rounded-full border border-[rgb(var(--ala-secondary))] px-6 py-4 font-heading text-sm font-medium text-[rgb(var(--ala-text))] transition-colors hover:bg-[rgb(var(--ala-secondary))]/10';

const secondaryCtaOnDarkClass =
  'group inline-flex w-fit items-center justify-center gap-2 rounded-full border border-[rgb(var(--ala-secondary))] px-6 py-4 font-heading text-sm font-medium text-[rgb(var(--ala-secondary))] transition-colors hover:bg-[rgb(var(--ala-secondary))]/10';

const aboutIntroCtaDef: SlotGroupDefinition = {
  label: 'Intro About CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/about-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'About Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const careersIntroCtaDef: SlotGroupDefinition = {
  label: 'Intro Careers CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/careers' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Our Careers' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const aboutJourneyCtaDef: SlotGroupDefinition = {
  label: 'Journey About CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/about-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'About Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const careersJourneyCtaDef: SlotGroupDefinition = {
  label: 'Journey Careers CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/careers' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Explore Careers' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const careersCtaDef: SlotGroupDefinition = {
  label: 'Careers CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/careers' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Open Roles' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const aboutCtaDef: SlotGroupDefinition = {
  label: 'About CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/about-us' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Learn About Us' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const impactHeroDef: SlotGroupDefinition = {
  label: 'Impact Hero Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue: IMPACT_HERO_IMAGE,
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community volunteers' },
  },
};

function makeImageDef(label: string, defaultValue: string, alt: string): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultValue },
      alt: { label: 'Alt Text', type: 'text', defaultValue: alt },
    },
  };
}

const ethosImg1Def = makeImageDef('Ethos Image 1', ETHOS_IMAGES[0], 'Integrity');
const ethosImg2Def = makeImageDef('Ethos Image 2', ETHOS_IMAGES[1], 'Understanding');
const ethosImg3Def = makeImageDef('Ethos Image 3', ETHOS_IMAGES[2], 'Equity & Diversity');
const ethosImg4Def = makeImageDef('Ethos Image 4', ETHOS_IMAGES[3], 'Unity');
const ethosImg5Def = makeImageDef('Ethos Image 5', ETHOS_IMAGES[4], 'Excellence');
const ethosImg6Def = makeImageDef('Ethos Image 6', ETHOS_IMAGES[5], 'Responsibility');
const ethosImg7Def = makeImageDef('Ethos Image 7', ETHOS_IMAGES[6], 'Environmental Stewardship');

const journeyImg1Def = makeImageDef('Journey Image 1', JOURNEY_IMAGES[0], 'Journey milestone');
const journeyImg2Def = makeImageDef('Journey Image 2', JOURNEY_IMAGES[1], 'Journey milestone');
const journeyImg3Def = makeImageDef('Journey Image 3', JOURNEY_IMAGES[2], 'Journey milestone');
const journeyImg4Def = makeImageDef('Journey Image 4', JOURNEY_IMAGES[3], 'Journey milestone');
const journeyImg5Def = makeImageDef('Journey Image 5', JOURNEY_IMAGES[4], 'Journey milestone');

const givingImg1Def = makeImageDef('Giving Image 1', GIVING_IMAGES[0], 'Giving Back Efforts');
const givingImg2Def = makeImageDef('Giving Image 2', GIVING_IMAGES[1], 'City Teaching Alliance');
const givingImg3Def = makeImageDef('Giving Image 3', GIVING_IMAGES[2], 'Brooks DeBartolo');

const marqueeImg1Def = makeImageDef('Slider Row 1 Image 1', MARQUEE_IMAGES[0], 'American Landmark team');
const marqueeImg2Def = makeImageDef('Slider Row 1 Image 2', MARQUEE_IMAGES[1], 'American Landmark team');
const marqueeImg3Def = makeImageDef('Slider Row 1 Image 3', MARQUEE_IMAGES[2], 'American Landmark team');
const marqueeImg4Def = makeImageDef('Slider Row 1 Image 4', MARQUEE_IMAGES[3], 'American Landmark team');
const marqueeImg5Def = makeImageDef('Slider Row 1 Image 5', MARQUEE_IMAGES[4], 'American Landmark team');
const marqueeImg6Def = makeImageDef('Slider Row 2 Image 1', '', 'American Landmark team');
const marqueeImg7Def = makeImageDef('Slider Row 2 Image 2', '', 'American Landmark team');
const marqueeImg8Def = makeImageDef('Slider Row 2 Image 3', '', 'American Landmark team');
const marqueeImg9Def = makeImageDef('Slider Row 2 Image 4', '', 'American Landmark team');
const marqueeImg10Def = makeImageDef('Slider Row 2 Image 5', '', 'American Landmark team');

function marqueeSize(index: number) {
  if (index % 3 === 0) return 'h-[384px] w-[384px]';
  if (index % 2 === 0) return 'h-[288px] w-[384px]';
  return 'h-[256px] w-[384px]';
}

type MarqueeSlide = { image?: string; alt?: string };

function CultureMarquees() {
  const row1 = [
    useSlotGroup('culturecontent.marquee_img_1', marqueeImg1Def),
    useSlotGroup('culturecontent.marquee_img_2', marqueeImg2Def),
    useSlotGroup('culturecontent.marquee_img_3', marqueeImg3Def),
    useSlotGroup('culturecontent.marquee_img_4', marqueeImg4Def),
    useSlotGroup('culturecontent.marquee_img_5', marqueeImg5Def),
  ];
  const row2Raw = [
    useSlotGroup('culturecontent.marquee_img_6', marqueeImg6Def),
    useSlotGroup('culturecontent.marquee_img_7', marqueeImg7Def),
    useSlotGroup('culturecontent.marquee_img_8', marqueeImg8Def),
    useSlotGroup('culturecontent.marquee_img_9', marqueeImg9Def),
    useSlotGroup('culturecontent.marquee_img_10', marqueeImg10Def),
  ];
  // Until row 2 has its own uploads, show row 1 in a shuffled order rather than blank tiles.
  const row2Fallback = [3, 0, 4, 1, 2];
  const row2 = row2Raw.map((slide, i) =>
    slide.image ? slide : row1[row2Fallback[i]],
  );

  return (
    <>
      <CultureMarquee
        direction={1}
        speed={50}
        firstSlot={1}
        slides={row1}
        defs={[marqueeImg1Def, marqueeImg2Def, marqueeImg3Def, marqueeImg4Def, marqueeImg5Def]}
      />
      <CultureMarquee
        direction={-1}
        speed={45}
        firstSlot={6}
        slides={row2}
        defs={[marqueeImg6Def, marqueeImg7Def, marqueeImg8Def, marqueeImg9Def, marqueeImg10Def]}
      />
    </>
  );
}

function CultureMarquee({
  direction,
  speed,
  firstSlot,
  slides,
  defs,
}: {
  direction: 1 | -1;
  speed: number;
  firstSlot: number;
  slides: MarqueeSlide[];
  defs: SlotGroupDefinition[];
}) {
  const items = [0, 1, 2, 3, 4, 0, 1, 2, 3, 4];

  return (
    <div className="flex w-full items-end gap-10 overflow-clip py-4">
      <motion.div
        className="flex shrink-0 items-end gap-10"
        animate={{ x: direction === 1 ? ['0%', '-50%'] : ['-50%', '0%'] }}
        transition={{ repeat: Infinity, ease: 'linear', duration: speed }}
      >
        {items.map((n, i) => (
          <EditableSlotGroup
            key={i}
            groupPath={`culturecontent.marquee_img_${firstSlot + n}`}
            definition={defs[n]}
          >
            <div className="shrink-0 overflow-clip rounded-lg bg-[#0a1a2e] transition-transform duration-500 hover:scale-[1.02]">
              <VibesitesImage
                url={slides[n].image || ''}
                alt={i < slides.length ? slides[n].alt || '' : ''}
                sizes="384px"
                className={`object-cover ${marqueeSize(i)}`}
              />
            </div>
          </EditableSlotGroup>
        ))}
      </motion.div>
    </div>
  );
}

function EthosCarousel() {
  const [index, setIndex] = useState(0);
  const total = 7;
  const next = () => setIndex((current) => (current + 1) % total);
  const prev = () => setIndex((current) => (current - 1 + total) % total);
  const peek = (index + 1) % total;

  const images = [
    useSlotGroup('culturecontent.ethos_img_1', ethosImg1Def),
    useSlotGroup('culturecontent.ethos_img_2', ethosImg2Def),
    useSlotGroup('culturecontent.ethos_img_3', ethosImg3Def),
    useSlotGroup('culturecontent.ethos_img_4', ethosImg4Def),
    useSlotGroup('culturecontent.ethos_img_5', ethosImg5Def),
    useSlotGroup('culturecontent.ethos_img_6', ethosImg6Def),
    useSlotGroup('culturecontent.ethos_img_7', ethosImg7Def),
  ];

  const defs = [
    ethosImg1Def,
    ethosImg2Def,
    ethosImg3Def,
    ethosImg4Def,
    ethosImg5Def,
    ethosImg6Def,
    ethosImg7Def,
  ];

  const years = [
    plainText(useContentToken('culturecontent.value_1_num')) || '01',
    plainText(useContentToken('culturecontent.value_2_num')) || '02',
    plainText(useContentToken('culturecontent.value_3_num')) || '03',
    plainText(useContentToken('culturecontent.value_4_num')) || '04',
    plainText(useContentToken('culturecontent.value_5_num')) || '05',
    plainText(useContentToken('culturecontent.value_6_num')) || '06',
    plainText(useContentToken('culturecontent.value_7_num')) || '07',
  ];

  const titles = [
    plainText(useContentToken('culturecontent.value_1_title')) || 'Integrity',
    plainText(useContentToken('culturecontent.value_2_title')) || 'Understanding',
    plainText(useContentToken('culturecontent.value_3_title')) || 'Equity & Diversity',
    plainText(useContentToken('culturecontent.value_4_title')) || 'Unity',
    plainText(useContentToken('culturecontent.value_5_title')) || 'Excellence',
    plainText(useContentToken('culturecontent.value_6_title')) || 'Responsibility',
    plainText(useContentToken('culturecontent.value_7_title')) || 'Environmental Stewardship',
  ];

  const navBtn =
    'flex h-12 w-12 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/30 bg-[rgb(var(--ala-on-primary))]/5 text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[rgb(var(--ala-on-primary))]/15';

  return (
    <div className="flex w-full flex-col gap-8 md:gap-10">
      <div className="flex items-stretch gap-3 md:gap-5">
        <div className="relative aspect-[16/10] min-w-0 flex-1 overflow-clip rounded-lg bg-[#0a1a2e]">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 80, scale: 0.92 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -80, scale: 0.92 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="absolute inset-0"
            >
              <EditableSlotGroup
                groupPath={`culturecontent.ethos_img_${index + 1}`}
                definition={defs[index]}
              >
                <VibesitesImage
                  url={images[index].image || ETHOS_IMAGES[index]}
                  alt={images[index].alt || titles[index]}
                  sizes="(min-width: 1024px) 60vw, 100vw"
                  className="h-full w-full object-cover"
                />
              </EditableSlotGroup>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex shrink-0 flex-col items-center justify-center gap-3">
          <button type="button" aria-label="Previous" onClick={prev} className={navBtn}>
            <ArrowLeft className="h-5 w-5" />
          </button>
          <button type="button" aria-label="Next" onClick={next} className={navBtn}>
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        <div className="relative hidden aspect-[16/10] w-[24%] shrink-0 overflow-clip rounded-lg bg-[#0a1a2e] opacity-70 lg:w-[22%] md:block">
          <AnimatePresence mode="wait">
            <motion.div
              key={peek}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0"
            >
              <VibesitesImage
                url={images[peek].image || ETHOS_IMAGES[peek]}
                alt=""
                sizes="22vw"
                className="h-full w-full object-cover"
              />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="relative w-full md:w-[68%]">
        {/* Hidden editors for inactive slides */}
        <div className="sr-only" aria-hidden>
          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
            <div key={n}>
              <RichTextContent path={`culturecontent.value_${n}_num`} label={`Value ${n} Number`} as="span" />
              <RichTextContent path={`culturecontent.value_${n}_title`} label={`Value ${n} Title`} as="span" />
              <RichTextContent path={`culturecontent.value_${n}_tagline`} label={`Value ${n} Tagline`} as="span" />
              <RichTextContent path={`culturecontent.value_${n}_body`} label={`Value ${n} Body`} as="span" />
              {n !== index + 1 ? (
                <EditableSlotGroup groupPath={`culturecontent.ethos_img_${n}`} definition={defs[n - 1]}>
                  <span />
                </EditableSlotGroup>
              ) : null}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3 }}
          >
            <p className="mb-2 block font-heading text-lg font-semibold text-[rgb(var(--ala-secondary))]">
              {years[index]} — {titles[index]}
            </p>
            <RichTextContent
              path={`culturecontent.value_${index + 1}_tagline`}
              label={`Value ${index + 1} Tagline`}
              as="h3"
              className="mb-4 font-heading text-[clamp(28px,4vw,56px)] font-medium leading-none text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path={`culturecontent.value_${index + 1}_body`}
              label={`Value ${index + 1} Body`}
              as="p"
              className="font-body text-lg font-normal leading-8 text-[#c9dcdc]"
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function QuotesCarousel() {
  const [index, setIndex] = useState(0);
  const total = 3;
  const prev = () => setIndex((current) => (current <= 0 ? total - 1 : current - 1));
  const next = () => setIndex((current) => (current + 1) % total);
  const quoteClass =
    'font-heading text-[clamp(18px,1.72vw,28px)] font-medium leading-[1.4] tracking-[-0.039em] text-[rgb(var(--ala-text))]';

  return (
    <section className="flex w-full justify-center overflow-clip bg-[rgb(var(--ala-surface))] px-4 py-20 md:px-8 md:py-32">
      <div className="flex w-full max-w-[1380px] flex-col gap-12">
        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded bg-[rgb(var(--ala-accent))]" aria-hidden />
            <RichTextContent
              path="culturecontent.quotes_eyebrow"
              label="Quotes Eyebrow"
              as="span"
              className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-[rgb(var(--ala-text))]"
            />
          </div>
          <RichTextContent
            path="culturecontent.quotes_headline"
            label="Quotes Headline"
            as="h2"
            className="font-heading text-[clamp(36px,4.2vw,60px)] font-medium leading-[1.1] tracking-[-0.05em] text-[rgb(var(--ala-text))]"
          />
        </div>

        <div className="relative border-t border-[rgb(var(--ala-accent))] pt-12">
          <div className="relative min-h-[8rem] pr-0 md:min-h-[10rem] md:pr-32">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                initial={false}
                animate={{
                  opacity: index === i ? 1 : 0,
                  x: index === i ? 0 : i < index ? -24 : 24,
                }}
                transition={{ duration: 0.45, ease: 'easeInOut' }}
                className={index === i ? 'relative' : 'pointer-events-none absolute inset-0'}
                aria-hidden={index !== i}
              >
                <RichTextContent
                  path={`culturecontent.quote_${i + 1}`}
                  label={`Quote ${i + 1}`}
                  as="p"
                  className={quoteClass}
                />
              </motion.div>
            ))}
          </div>

          <div className="relative mt-6 flex justify-end gap-4 md:absolute md:right-0 md:top-6 md:mt-0">
            <button
              type="button"
              aria-label="Previous quote"
              onClick={prev}
              className="flex h-[52px] w-[60px] items-center justify-center rounded bg-[rgb(var(--ala-border))]/60 transition-colors hover:bg-[rgb(var(--ala-border))]"
            >
              <ArrowLeft className="h-4 w-4 text-[rgb(var(--ala-text-muted))]" />
            </button>
            <button
              type="button"
              aria-label="Next quote"
              onClick={next}
              className="flex h-[52px] w-[60px] items-center justify-center rounded bg-[rgb(var(--ala-border))]/60 transition-colors hover:bg-[rgb(var(--ala-border))]"
            >
              <ArrowRight className="h-4 w-4 text-[rgb(var(--ala-text-muted))]" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function JourneyCarousel() {
  const [index, setIndex] = useState(0);
  const total = 5;
  const next = () => setIndex((current) => (current + 1) % total);
  const prev = () => setIndex((current) => (current - 1 + total) % total);
  const left = (index - 1 + total) % total;
  const right = (index + 1) % total;

  const images = [
    useSlotGroup('culturecontent.journey_img_1', journeyImg1Def),
    useSlotGroup('culturecontent.journey_img_2', journeyImg2Def),
    useSlotGroup('culturecontent.journey_img_3', journeyImg3Def),
    useSlotGroup('culturecontent.journey_img_4', journeyImg4Def),
    useSlotGroup('culturecontent.journey_img_5', journeyImg5Def),
  ];
  const defs = [journeyImg1Def, journeyImg2Def, journeyImg3Def, journeyImg4Def, journeyImg5Def];

  const years = [
    plainText(useContentToken('culturecontent.journey_1_year')) || '2016',
    plainText(useContentToken('culturecontent.journey_2_year')) || '2018',
    plainText(useContentToken('culturecontent.journey_3_year')) || '2020',
    plainText(useContentToken('culturecontent.journey_4_year')) || '2022',
    plainText(useContentToken('culturecontent.journey_5_year')) || '2024',
  ];

  const navBtn =
    'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[rgb(var(--ala-text))]/20 bg-[rgb(var(--ala-text))]/5 text-[rgb(var(--ala-text))] transition-colors hover:bg-[rgb(var(--ala-text))]/15 md:h-12 md:w-12';

  return (
    <div className="flex w-full flex-col items-center gap-10 md:gap-12">
      <div className="relative flex h-[280px] w-full max-w-[1100px] items-center justify-center sm:h-[360px] md:h-[460px]">
        <div className="absolute -bottom-4 left-1/2 h-1 w-[240px] -translate-x-1/2 overflow-hidden rounded-full bg-[rgb(var(--ala-text))]/15 sm:w-[310px] md:w-[420px]">
          <motion.div
            key={index}
            className="h-full rounded-full bg-[rgb(var(--ala-secondary))]"
            initial={{ width: '0%' }}
            animate={{ width: `${((index + 1) / total) * 100}%` }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          />
        </div>

        <div className="absolute left-0 top-1/2 aspect-square w-[150px] -translate-y-1/2 overflow-clip rounded-lg opacity-40 sm:w-[210px] md:w-[280px]">
          <VibesitesImage
            url={images[left].image || JOURNEY_IMAGES[left]}
            alt=""
            sizes="280px"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>
        <div className="absolute right-0 top-1/2 aspect-square w-[150px] -translate-y-1/2 overflow-clip rounded-lg opacity-40 sm:w-[210px] md:w-[280px]">
          <VibesitesImage
            url={images[right].image || JOURNEY_IMAGES[right]}
            alt=""
            sizes="280px"
            className="absolute inset-0 h-full w-full object-cover"
          />
        </div>

        <div className="relative z-10 flex items-center gap-4 md:gap-6">
          <button type="button" aria-label="Previous" onClick={prev} className={navBtn}>
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="relative aspect-square w-[240px] overflow-clip rounded-lg bg-[#0a1a2e] shadow-2xl sm:w-[310px] md:w-[420px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                className="absolute inset-0"
              >
                <EditableSlotGroup
                  groupPath={`culturecontent.journey_img_${index + 1}`}
                  definition={defs[index]}
                >
                  <div className="absolute inset-0">
                    <VibesitesImage
                      url={images[index].image || JOURNEY_IMAGES[index]}
                      alt={images[index].alt || ''}
                      sizes="420px"
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                  </div>
                </EditableSlotGroup>
              </motion.div>
            </AnimatePresence>
          </div>
          <button type="button" aria-label="Next" onClick={next} className={navBtn}>
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="w-full max-w-[620px] text-center">
        <div className="sr-only" aria-hidden>
          {[1, 2, 3, 4, 5].map((n) => (
            <div key={n}>
              <RichTextContent path={`culturecontent.journey_${n}_year`} label={`Journey ${n} Year`} as="span" />
              <RichTextContent path={`culturecontent.journey_${n}_title`} label={`Journey ${n} Title`} as="span" />
              <RichTextContent path={`culturecontent.journey_${n}_body`} label={`Journey ${n} Body`} as="span" />
              {n !== index + 1 ? (
                <EditableSlotGroup groupPath={`culturecontent.journey_img_${n}`} definition={defs[n - 1]}>
                  <span />
                </EditableSlotGroup>
              ) : null}
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-4"
          >
            <div className="flex items-center gap-2 rounded-full border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-surface))] px-4 py-1">
              <span className="h-2 w-2 rounded-full bg-[#f0c557] shadow-[0_0_6px_rgba(240,197,87,1)]" aria-hidden />
              <span className="font-body text-sm font-medium text-[rgb(var(--ala-text))]">{years[index]}</span>
            </div>
            <RichTextContent
              path={`culturecontent.journey_${index + 1}_title`}
              label={`Journey ${index + 1} Title`}
              as="h3"
              className="font-body text-xl font-medium text-[rgb(var(--ala-text))] md:text-2xl"
            />
            <RichTextContent
              path={`culturecontent.journey_${index + 1}_body`}
              label={`Journey ${index + 1} Body`}
              as="p"
              className="font-body text-lg font-normal leading-[1.6] text-[rgb(var(--ala-text))]/70"
            />
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

function CommunityImpactSection() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const total = 3;
  const impactHero = useSlotGroup('culturecontent.impact_hero', impactHeroDef);
  const givingImages = [
    useSlotGroup('culturecontent.giving_img_1', givingImg1Def),
    useSlotGroup('culturecontent.giving_img_2', givingImg2Def),
    useSlotGroup('culturecontent.giving_img_3', givingImg3Def),
  ];
  const givingDefs = [givingImg1Def, givingImg2Def, givingImg3Def];

  const go = (dir: number) => {
    setDirection(dir);
    setIndex((current) => (current + dir + total) % total);
  };

  const stats = [
    {
      value: plainText(useContentToken('culturecontent.stat_1_value')) || '214+',
      labelPath: 'culturecontent.stat_1_label',
      label: 'Stat 1 Label',
    },
    {
      value: plainText(useContentToken('culturecontent.stat_2_value')) || '12,744',
      labelPath: 'culturecontent.stat_2_label',
      label: 'Stat 2 Label',
    },
    {
      value: plainText(useContentToken('culturecontent.stat_3_value')) || '3,263',
      labelPath: 'culturecontent.stat_3_label',
      label: 'Stat 3 Label',
    },
    {
      value: plainText(useContentToken('culturecontent.stat_4_value')) || '34',
      labelPath: 'culturecontent.stat_4_label',
      label: 'Stat 4 Label',
    },
  ];

  return (
    <section className="relative w-full overflow-clip bg-[rgb(var(--ala-dark))]">
      <style>{`
        .culture-ci-stroke text {
          font-size: 180px;
          stroke-width: 4;
          letter-spacing: -4px;
        }
        .culture-ci-stroke text.culture-ci-logo {
          stroke: #ffffff;
          animation: 4.5s infinite alternate culture-ci-stroke-anim;
        }
        @keyframes culture-ci-stroke-anim {
          0% { fill: transparent; stroke: #ffffff; stroke-width: 3; stroke-dashoffset: 25%; stroke-dasharray: 0 32%; }
          50% { fill: transparent; stroke: #ffffff; stroke-width: 3; }
          80%, 100% { fill: rgba(255,255,255,0.95); stroke: transparent; stroke-width: 0; stroke-dashoffset: -25%; stroke-dasharray: 32% 0; }
        }
      `}</style>

      <div className="relative flex h-[60vh] min-h-[420px] w-full items-center justify-center md:h-[70vh]">
        <EditableSlotGroup groupPath="culturecontent.impact_hero" definition={impactHeroDef}>
          <div className="absolute inset-0">
            <VibesitesImage
              url={impactHero.image || IMPACT_HERO_IMAGE}
              alt={impactHero.alt || ''}
              sizes="100vw"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-[rgb(var(--ala-dark))]/55" />
          </div>
        </EditableSlotGroup>
        <div className="relative z-10 mx-auto w-full max-w-[1440px] px-4">
          <svg
            className="culture-ci-stroke block h-auto w-full"
            viewBox="0 0 2400 420"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden
          >
            <text
              x="50%"
              y="50%"
              dy=".32em"
              textAnchor="middle"
              className="culture-ci-logo"
              fill="#ffffff"
              style={{ fontSize: '180px', fontWeight: 800 }}
            >
              COMMUNITY IMPACT
            </text>
          </svg>
          <span className="sr-only">
            <RichTextContent path="culturecontent.impact_banner" label="Impact Banner" as="span" />
          </span>
        </div>
      </div>

      <div className="relative z-10 w-full border-t border-[rgb(var(--ala-on-primary))]/10 bg-[rgb(var(--ala-dark))]">
        <div className="mx-auto grid max-w-[1440px] grid-cols-2 px-4 md:grid-cols-4 md:px-8">
          {stats.map((stat, i) => (
            <div
              key={stat.labelPath}
              className={`flex flex-col items-center gap-2 px-4 py-8 text-center md:py-12 ${
                i < stats.length - 1 ? 'md:border-r md:border-[rgb(var(--ala-on-primary))]/10' : ''
              } ${i % 2 === 0 ? 'border-r border-[rgb(var(--ala-on-primary))]/10' : ''} ${
                i < 2 ? 'border-b border-[rgb(var(--ala-on-primary))]/10 md:border-b-0' : ''
              }`}
            >
              <CountUp
                value={stat.value}
                className="font-heading text-[clamp(32px,3.4vw,56px)] font-bold leading-none tracking-[-0.04em] text-[rgb(var(--ala-on-primary))]"
              />
              <span className="sr-only">
                <RichTextContent path={`culturecontent.stat_${i + 1}_value`} label={`Stat ${i + 1} Value`} as="span" />
              </span>
              <RichTextContent
                path={stat.labelPath}
                label={stat.label}
                as="span"
                className="font-heading text-base font-normal text-[rgb(var(--ala-on-primary))]/60"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="w-full bg-[rgb(var(--ala-dark))] px-4 py-16 md:px-8 md:py-24">
        <div className="mx-auto flex max-w-[1440px] flex-col justify-between gap-8 md:flex-row md:items-end">
          <RichTextContent
            path="culturecontent.impact_intro"
            label="Impact Intro"
            as="p"
            className="max-w-[860px] font-heading text-[clamp(20px,1.9vw,30px)] font-normal leading-[1.3] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
          />
          <div className="flex shrink-0 items-center gap-4">
            <RichTextContent
              path="culturecontent.impact_nav_label"
              label="Impact Nav Label"
              as="span"
              className="font-heading text-sm font-medium uppercase tracking-[0.15em] text-[rgb(var(--ala-on-primary))]/50"
            />
            <div className="flex gap-2">
              <button
                type="button"
                aria-label="Previous slide"
                onClick={() => go(-1)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/30 text-white transition-colors hover:border-[rgb(var(--ala-secondary))] hover:bg-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-on-secondary))]"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                aria-label="Next slide"
                onClick={() => go(1)}
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[rgb(var(--ala-on-primary))]/30 text-white transition-colors hover:border-[rgb(var(--ala-secondary))] hover:bg-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-on-secondary))]"
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 pb-20 md:px-8 md:pb-28">
        <div className="mx-auto max-w-[1440px]">
          <div className="sr-only" aria-hidden>
            {[1, 2, 3].map((n) => (
              <div key={n}>
                <RichTextContent path={`culturecontent.giving_${n}_eyebrow`} label={`Giving ${n} Eyebrow`} as="span" />
                <RichTextContent path={`culturecontent.giving_${n}_title`} label={`Giving ${n} Title`} as="span" />
                <RichTextContent path={`culturecontent.giving_${n}_points`} label={`Giving ${n} Points`} as="span" />
                {n !== index + 1 ? (
                  <EditableSlotGroup groupPath={`culturecontent.giving_img_${n}`} definition={givingDefs[n - 1]}>
                    <span />
                  </EditableSlotGroup>
                ) : null}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={index}
              custom={direction}
              initial={{ opacity: 0, x: direction * 60 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: direction * -60 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="grid grid-cols-1 overflow-clip rounded-2xl border border-[rgb(var(--ala-on-primary))]/10 bg-[rgb(var(--ala-on-primary))]/[0.04] lg:grid-cols-2"
            >
              <EditableSlotGroup
                groupPath={`culturecontent.giving_img_${index + 1}`}
                definition={givingDefs[index]}
              >
                <div className="relative h-[280px] w-full lg:h-auto lg:min-h-[440px]">
                  <VibesitesImage
                    url={givingImages[index].image || GIVING_IMAGES[index]}
                    alt={givingImages[index].alt || ''}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                </div>
              </EditableSlotGroup>
              <div className="flex flex-col justify-center gap-5 p-8 md:p-12">
                <RichTextContent
                  path={`culturecontent.giving_${index + 1}_eyebrow`}
                  label={`Giving ${index + 1} Eyebrow`}
                  as="span"
                  className="font-heading text-sm font-semibold uppercase tracking-[0.15em] text-[#7AB3E5]"
                />
                <RichTextContent
                  path={`culturecontent.giving_${index + 1}_title`}
                  label={`Giving ${index + 1} Title`}
                  as="h3"
                  className="font-heading text-[clamp(26px,2.6vw,40px)] font-bold leading-tight tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
                />
                <RichTextContent
                  path={`culturecontent.giving_${index + 1}_points`}
                  label={`Giving ${index + 1} Points`}
                  as="div"
                  className="font-heading text-base font-normal leading-6 text-[rgb(var(--ala-on-primary))]/70 [&_li]:mb-3 [&_li]:flex [&_li]:gap-3 [&_li]:before:mt-2.5 [&_li]:before:h-1.5 [&_li]:before:w-1.5 [&_li]:before:shrink-0 [&_li]:before:rounded-full [&_li]:before:bg-[rgb(var(--ala-secondary))] [&_li]:before:content-[''] [&_ul]:list-none [&_ul]:p-0"
                />
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex justify-center gap-2">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => {
                  setDirection(i > index ? 1 : -1);
                  setIndex(i);
                }}
                className={`h-2 rounded-full transition-all ${
                  i === index ? 'w-8 bg-[rgb(var(--ala-secondary))]' : 'w-2 bg-[rgb(var(--ala-on-primary))]/25'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ArtistsTimeline() {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start center', 'end center'],
  });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  const items = [1, 2, 3, 4, 5];

  return (
    <section className="w-full overflow-clip bg-[#F7F7F7] py-24 md:py-32">
      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 gap-12 px-6 md:grid-cols-2 md:gap-20 md:px-8">
        <div className="flex flex-col gap-6 md:sticky md:top-44 md:self-start lg:top-48">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded bg-[rgb(var(--ala-accent))]" aria-hidden />
            <RichTextContent
              path="culturecontent.air_eyebrow"
              label="AIR Eyebrow"
              as="span"
              className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-[rgb(var(--ala-text-muted))]"
            />
          </div>
          <RichTextContent
            path="culturecontent.air_headline"
            label="AIR Headline"
            as="h2"
            className="font-heading text-[clamp(33px,3.69vw,60px)] font-medium leading-[1.1] tracking-[-0.05em] text-[rgb(var(--ala-text))]"
          />
          <div className="flex max-w-[460px] flex-col gap-4">
            <RichTextContent
              path="culturecontent.air_body_1"
              label="AIR Body 1"
              as="p"
              className="font-heading text-lg font-normal leading-6 text-[rgb(var(--ala-text-muted))]"
            />
            <RichTextContent
              path="culturecontent.air_body_2"
              label="AIR Body 2"
              as="p"
              className="font-heading text-lg font-normal leading-6 text-[rgb(var(--ala-text-muted))]"
            />
            <RichTextContent
              path="culturecontent.air_body_3"
              label="AIR Body 3"
              as="p"
              className="font-heading text-lg font-normal leading-6 text-[rgb(var(--ala-text-muted))]"
            />
          </div>
        </div>

        <div ref={ref} className="relative flex w-full flex-col gap-12 pl-8 md:gap-16">
          <div className="absolute bottom-2 left-[7px] top-2 w-[2px] bg-[#D1D5DB]" />
          <motion.div
            style={{ scaleY }}
            className="absolute bottom-2 left-[7px] top-2 z-10 w-[2px] origin-top bg-[rgb(var(--ala-accent))]"
          />
          {items.map((n) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.6 }}
              className="relative flex flex-col gap-2"
            >
              <div className="absolute -left-8 top-2 z-20">
                <div className="h-4 w-4 rounded-full border-[3px] border-[rgb(var(--ala-accent))] bg-[rgb(var(--ala-background))] shadow-md" />
              </div>
              <RichTextContent
                path={`culturecontent.air_t${n}_label`}
                label={`AIR Timeline ${n} Label`}
                as="span"
                className="font-heading text-sm font-semibold uppercase tracking-[0.12em] text-[rgb(var(--ala-accent))]"
              />
              <RichTextContent
                path={`culturecontent.air_t${n}_title`}
                label={`AIR Timeline ${n} Title`}
                as="h3"
                className="font-heading text-[clamp(22px,1.9vw,30px)] font-semibold leading-tight tracking-[-0.03em] text-[rgb(var(--ala-text))]"
              />
              <RichTextContent
                path={`culturecontent.air_t${n}_body`}
                label={`AIR Timeline ${n} Body`}
                as="p"
                className="max-w-[460px] font-heading text-base font-normal leading-6 text-[rgb(var(--ala-text-muted))]"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CultureContent() {
  const aboutIntroCta = useSlotGroup('culturecontent.about_intro_cta', aboutIntroCtaDef);
  const careersIntroCta = useSlotGroup('culturecontent.careers_intro_cta', careersIntroCtaDef);
  const aboutJourneyCta = useSlotGroup('culturecontent.about_journey_cta', aboutJourneyCtaDef);
  const careersJourneyCta = useSlotGroup('culturecontent.careers_journey_cta', careersJourneyCtaDef);
  const careersCta = useSlotGroup('culturecontent.careers_cta', careersCtaDef);
  const aboutCta = useSlotGroup('culturecontent.about_cta', aboutCtaDef);

  // Silence unused warning if tree-shaken incorrectly — keep hooks for editor bindings
  useEffect(() => undefined, []);

  return (
    <>
      {/* People make the place */}
      <section className="relative mx-auto flex w-full max-w-[1440px] flex-col items-center px-6 py-24 text-center md:px-12">
        <motion.h2
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
          }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          className="relative z-10 mb-12 flex max-w-[700px] flex-wrap justify-center gap-x-[0.28em] gap-y-1 font-heading text-[clamp(40px,6.67vw,96px)] font-bold lowercase leading-[1.05] text-[rgb(var(--ala-text))]"
        >
          <span className="sr-only">
            <RichTextContent path="culturecontent.place_headline" label="Place Headline" as="span" />
          </span>
          {PLACE_WORDS.map((word) => (
            <motion.span key={word} variants={wordVariants} className="inline-block origin-bottom">
              {word}
            </motion.span>
          ))}
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <RichTextContent
            path="culturecontent.intro"
            label="Intro"
            as="p"
            className="mb-12 max-w-[823px] font-heading text-[clamp(18px,1.4vw,21px)] font-normal leading-[1.4] text-[rgb(var(--ala-text))]"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={alaViewport}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex flex-col items-center gap-4 sm:flex-row"
        >
          <EditableSlotGroup groupPath="culturecontent.about_intro_cta" definition={aboutIntroCtaDef}>
            <a
              href={aboutIntroCta.url || '/about-us'}
              target={aboutIntroCta._target || '_self'}
              className={primaryCtaClass}
            >
              <span>{aboutIntroCta.label || 'About Us'}</span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </a>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="culturecontent.careers_intro_cta" definition={careersIntroCtaDef}>
            <a
              href={careersIntroCta.url || '/careers'}
              target={careersIntroCta._target || '_self'}
              className={secondaryCtaClass}
            >
              {careersIntroCta.label || 'Our Careers'}
            </a>
          </EditableSlotGroup>
        </motion.div>
      </section>

      {/* Lifestyle marquees */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-surface))] py-12">
        <CultureMarquees />
      </section>

      {/* Ethos */}
      <section className="w-full overflow-clip bg-[#091931] px-6 py-24 md:px-20">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-16">
          <div className="flex flex-col gap-2">
            <RichTextContent
              path="culturecontent.values_eyebrow"
              label="Values Eyebrow"
              as="p"
              className="font-heading text-xl font-medium text-[rgb(var(--ala-on-primary))]"
            />
            <RichTextContent
              path="culturecontent.values_headline"
              label="Values Headline"
              as="h2"
              className="font-heading text-[clamp(36px,5.76vw,83px)] font-medium leading-[1.01] text-[rgb(var(--ala-on-primary))]"
            />
          </div>
          <EthosCarousel />
        </div>
      </section>

      <QuotesCarousel />

      {/* Journey */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-surface))] px-6 py-24 md:px-20">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-16">
          <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
            <RichTextContent
              path="culturecontent.journey_headline"
              label="Journey Headline"
              as="h2"
              className="text-left font-heading text-[clamp(40px,6.11vw,88px)] font-medium leading-[0.95] text-[rgb(var(--ala-text))]"
            />
            <div className="flex items-center gap-4">
              <EditableSlotGroup groupPath="culturecontent.about_journey_cta" definition={aboutJourneyCtaDef}>
                <a
                  href={aboutJourneyCta.url || '/about-us'}
                  target={aboutJourneyCta._target || '_self'}
                  className={`${primaryCtaClass} whitespace-nowrap`}
                >
                  <span>{aboutJourneyCta.label || 'About Us'}</span>
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup
                groupPath="culturecontent.careers_journey_cta"
                definition={careersJourneyCtaDef}
              >
                <a
                  href={careersJourneyCta.url || '/careers'}
                  target={careersJourneyCta._target || '_self'}
                  className={`${secondaryCtaOnDarkClass} whitespace-nowrap`}
                >
                  {careersJourneyCta.label || 'Explore Careers'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
          <JourneyCarousel />
        </div>
      </section>

      <CommunityImpactSection />
      <ArtistsTimeline />

      {/* Careers CTA */}
      <section className="w-full overflow-clip bg-[rgb(var(--ala-dark))] px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto flex max-w-[1440px] flex-col items-center gap-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.6 }}
          >
            <RichTextContent
              path="culturecontent.cta_eyebrow"
              label="CTA Eyebrow"
              as="p"
              className="font-heading text-base font-medium uppercase tracking-[2px] text-[rgb(var(--ala-secondary))]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <RichTextContent
              path="culturecontent.cta_headline"
              label="CTA Headline"
              as="h2"
              className="max-w-[900px] font-heading text-[clamp(36px,5.2vw,76px)] font-medium leading-[1.05] tracking-[-0.03em] text-[rgb(var(--ala-on-primary))]"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <RichTextContent
              path="culturecontent.cta_body"
              label="CTA Body"
              as="p"
              className="max-w-[760px] font-heading text-[clamp(18px,1.4vw,21px)] font-normal leading-[1.4] text-[rgb(var(--ala-on-primary))]/70"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={alaViewport}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="flex flex-col items-center gap-4 pt-2 sm:flex-row"
          >
            <EditableSlotGroup groupPath="culturecontent.careers_cta" definition={careersCtaDef}>
              <a
                href={careersCta.url || '/careers'}
                target={careersCta._target || '_self'}
                className={primaryCtaClass}
              >
                <span>{careersCta.label || 'View Open Roles'}</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="culturecontent.about_cta" definition={aboutCtaDef}>
              <a
                href={aboutCta.url || '/about-us'}
                target={aboutCta._target || '_self'}
                className="group inline-flex w-fit items-center justify-center gap-2 rounded-full border border-[rgb(var(--ala-on-primary))]/40 px-6 py-4 font-heading text-sm font-medium text-[rgb(var(--ala-on-primary))] transition-colors hover:bg-[rgb(var(--ala-on-primary))]/10"
              >
                <span>{aboutCta.label || 'Learn About Us'}</span>
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </a>
            </EditableSlotGroup>
          </motion.div>
        </div>
      </section>

      {/* Schema anchors — literal paths for tokens/slot groups rendered via templates */}
      <div className="hidden" aria-hidden style={{ display: 'none' }}>
        <RichTextContent path="culturecontent.value_1_num" label="Value 1 Number" as="span" />
        <RichTextContent path="culturecontent.value_1_title" label="Value 1 Title" as="span" />
        <RichTextContent path="culturecontent.value_1_tagline" label="Value 1 Tagline" as="span" />
        <RichTextContent path="culturecontent.value_1_body" label="Value 1 Body" as="span" />
        <RichTextContent path="culturecontent.value_2_num" label="Value 2 Number" as="span" />
        <RichTextContent path="culturecontent.value_2_title" label="Value 2 Title" as="span" />
        <RichTextContent path="culturecontent.value_2_tagline" label="Value 2 Tagline" as="span" />
        <RichTextContent path="culturecontent.value_2_body" label="Value 2 Body" as="span" />
        <RichTextContent path="culturecontent.value_3_num" label="Value 3 Number" as="span" />
        <RichTextContent path="culturecontent.value_3_title" label="Value 3 Title" as="span" />
        <RichTextContent path="culturecontent.value_3_tagline" label="Value 3 Tagline" as="span" />
        <RichTextContent path="culturecontent.value_3_body" label="Value 3 Body" as="span" />
        <RichTextContent path="culturecontent.value_4_num" label="Value 4 Number" as="span" />
        <RichTextContent path="culturecontent.value_4_title" label="Value 4 Title" as="span" />
        <RichTextContent path="culturecontent.value_4_tagline" label="Value 4 Tagline" as="span" />
        <RichTextContent path="culturecontent.value_4_body" label="Value 4 Body" as="span" />
        <RichTextContent path="culturecontent.value_5_num" label="Value 5 Number" as="span" />
        <RichTextContent path="culturecontent.value_5_title" label="Value 5 Title" as="span" />
        <RichTextContent path="culturecontent.value_5_tagline" label="Value 5 Tagline" as="span" />
        <RichTextContent path="culturecontent.value_5_body" label="Value 5 Body" as="span" />
        <RichTextContent path="culturecontent.value_6_num" label="Value 6 Number" as="span" />
        <RichTextContent path="culturecontent.value_6_title" label="Value 6 Title" as="span" />
        <RichTextContent path="culturecontent.value_6_tagline" label="Value 6 Tagline" as="span" />
        <RichTextContent path="culturecontent.value_6_body" label="Value 6 Body" as="span" />
        <RichTextContent path="culturecontent.value_7_num" label="Value 7 Number" as="span" />
        <RichTextContent path="culturecontent.value_7_title" label="Value 7 Title" as="span" />
        <RichTextContent path="culturecontent.value_7_tagline" label="Value 7 Tagline" as="span" />
        <RichTextContent path="culturecontent.value_7_body" label="Value 7 Body" as="span" />
        <RichTextContent path="culturecontent.quote_1" label="Quote 1" as="span" />
        <RichTextContent path="culturecontent.quote_2" label="Quote 2" as="span" />
        <RichTextContent path="culturecontent.quote_3" label="Quote 3" as="span" />
        <RichTextContent path="culturecontent.journey_1_year" label="Journey 1 Year" as="span" />
        <RichTextContent path="culturecontent.journey_1_title" label="Journey 1 Title" as="span" />
        <RichTextContent path="culturecontent.journey_1_body" label="Journey 1 Body" as="span" />
        <RichTextContent path="culturecontent.journey_2_year" label="Journey 2 Year" as="span" />
        <RichTextContent path="culturecontent.journey_2_title" label="Journey 2 Title" as="span" />
        <RichTextContent path="culturecontent.journey_2_body" label="Journey 2 Body" as="span" />
        <RichTextContent path="culturecontent.journey_3_year" label="Journey 3 Year" as="span" />
        <RichTextContent path="culturecontent.journey_3_title" label="Journey 3 Title" as="span" />
        <RichTextContent path="culturecontent.journey_3_body" label="Journey 3 Body" as="span" />
        <RichTextContent path="culturecontent.journey_4_year" label="Journey 4 Year" as="span" />
        <RichTextContent path="culturecontent.journey_4_title" label="Journey 4 Title" as="span" />
        <RichTextContent path="culturecontent.journey_4_body" label="Journey 4 Body" as="span" />
        <RichTextContent path="culturecontent.journey_5_year" label="Journey 5 Year" as="span" />
        <RichTextContent path="culturecontent.journey_5_title" label="Journey 5 Title" as="span" />
        <RichTextContent path="culturecontent.journey_5_body" label="Journey 5 Body" as="span" />
        <RichTextContent path="culturecontent.stat_1_value" label="Stat 1 Value" as="span" />
        <RichTextContent path="culturecontent.stat_1_label" label="Stat 1 Label" as="span" />
        <RichTextContent path="culturecontent.stat_2_value" label="Stat 2 Value" as="span" />
        <RichTextContent path="culturecontent.stat_2_label" label="Stat 2 Label" as="span" />
        <RichTextContent path="culturecontent.stat_3_value" label="Stat 3 Value" as="span" />
        <RichTextContent path="culturecontent.stat_3_label" label="Stat 3 Label" as="span" />
        <RichTextContent path="culturecontent.stat_4_value" label="Stat 4 Value" as="span" />
        <RichTextContent path="culturecontent.stat_4_label" label="Stat 4 Label" as="span" />
        <RichTextContent path="culturecontent.giving_1_eyebrow" label="Giving 1 Eyebrow" as="span" />
        <RichTextContent path="culturecontent.giving_1_title" label="Giving 1 Title" as="span" />
        <RichTextContent path="culturecontent.giving_1_points" label="Giving 1 Points" as="span" />
        <RichTextContent path="culturecontent.giving_2_eyebrow" label="Giving 2 Eyebrow" as="span" />
        <RichTextContent path="culturecontent.giving_2_title" label="Giving 2 Title" as="span" />
        <RichTextContent path="culturecontent.giving_2_points" label="Giving 2 Points" as="span" />
        <RichTextContent path="culturecontent.giving_3_eyebrow" label="Giving 3 Eyebrow" as="span" />
        <RichTextContent path="culturecontent.giving_3_title" label="Giving 3 Title" as="span" />
        <RichTextContent path="culturecontent.giving_3_points" label="Giving 3 Points" as="span" />
        <RichTextContent path="culturecontent.air_t1_label" label="AIR Timeline 1 Label" as="span" />
        <RichTextContent path="culturecontent.air_t1_title" label="AIR Timeline 1 Title" as="span" />
        <RichTextContent path="culturecontent.air_t1_body" label="AIR Timeline 1 Body" as="span" />
        <RichTextContent path="culturecontent.air_t2_label" label="AIR Timeline 2 Label" as="span" />
        <RichTextContent path="culturecontent.air_t2_title" label="AIR Timeline 2 Title" as="span" />
        <RichTextContent path="culturecontent.air_t2_body" label="AIR Timeline 2 Body" as="span" />
        <RichTextContent path="culturecontent.air_t3_label" label="AIR Timeline 3 Label" as="span" />
        <RichTextContent path="culturecontent.air_t3_title" label="AIR Timeline 3 Title" as="span" />
        <RichTextContent path="culturecontent.air_t3_body" label="AIR Timeline 3 Body" as="span" />
        <RichTextContent path="culturecontent.air_t4_label" label="AIR Timeline 4 Label" as="span" />
        <RichTextContent path="culturecontent.air_t4_title" label="AIR Timeline 4 Title" as="span" />
        <RichTextContent path="culturecontent.air_t4_body" label="AIR Timeline 4 Body" as="span" />
        <RichTextContent path="culturecontent.air_t5_label" label="AIR Timeline 5 Label" as="span" />
        <RichTextContent path="culturecontent.air_t5_title" label="AIR Timeline 5 Title" as="span" />
        <RichTextContent path="culturecontent.air_t5_body" label="AIR Timeline 5 Body" as="span" />
        <EditableSlotGroup groupPath="culturecontent.ethos_img_1" definition={ethosImg1Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.ethos_img_2" definition={ethosImg2Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.ethos_img_3" definition={ethosImg3Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.ethos_img_4" definition={ethosImg4Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.ethos_img_5" definition={ethosImg5Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.ethos_img_6" definition={ethosImg6Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.ethos_img_7" definition={ethosImg7Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.journey_img_1" definition={journeyImg1Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.journey_img_2" definition={journeyImg2Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.journey_img_3" definition={journeyImg3Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.journey_img_4" definition={journeyImg4Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.journey_img_5" definition={journeyImg5Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.giving_img_1" definition={givingImg1Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.giving_img_2" definition={givingImg2Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.giving_img_3" definition={givingImg3Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_1" definition={marqueeImg1Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_2" definition={marqueeImg2Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_3" definition={marqueeImg3Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_4" definition={marqueeImg4Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_5" definition={marqueeImg5Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_6" definition={marqueeImg6Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_7" definition={marqueeImg7Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_8" definition={marqueeImg8Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_9" definition={marqueeImg9Def}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="culturecontent.marquee_img_10" definition={marqueeImg10Def}>
          <span />
        </EditableSlotGroup>
      </div>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CultureContent',
  displayName: 'Culture Content',
  tokens: {
    'culturecontent.place_headline': {
      label: 'Place Headline',
      type: 'richtext',
      defaultValue: '<p>people make the place.</p>',
    },
    'culturecontent.intro': {
      label: 'Intro',
      type: 'richtext',
      defaultValue:
        "<p>Great communities don't happen by accident. They're built by people who care, challenge each other, celebrate the wins, and show up for one another. At American Landmark, our culture is about creating a place where talented people can do meaningful work, build real connections, and have a little fun along the way.</p>",
    },
    'culturecontent.values_eyebrow': {
      label: 'Values Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Our Ethos and Values</p>',
    },
    'culturecontent.values_headline': {
      label: 'Values Headline',
      type: 'richtext',
      defaultValue: "<p>So... what's it like here?</p>",
    },
    'culturecontent.value_1_num': { label: 'Value 1 Number', type: 'richtext', defaultValue: '<p>01</p>' },
    'culturecontent.value_1_title': { label: 'Value 1 Title', type: 'richtext', defaultValue: '<p>Integrity</p>' },
    'culturecontent.value_1_tagline': {
      label: 'Value 1 Tagline',
      type: 'richtext',
      defaultValue: '<p>Do the right thing. Always.</p>',
    },
    'culturecontent.value_1_body': {
      label: 'Value 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>We believe trust starts with honesty. We conduct our business with transparency, keep our word, and hold ourselves accountable for the decisions we make.</p>',
    },
    'culturecontent.value_2_num': { label: 'Value 2 Number', type: 'richtext', defaultValue: '<p>02</p>' },
    'culturecontent.value_2_title': { label: 'Value 2 Title', type: 'richtext', defaultValue: '<p>Understanding</p>' },
    'culturecontent.value_2_tagline': {
      label: 'Value 2 Tagline',
      type: 'richtext',
      defaultValue: '<p>Lead with care.</p>',
    },
    'culturecontent.value_2_body': {
      label: 'Value 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>People come first. We listen, show empathy, and treat our residents, team members, partners, and shareholders with the respect and humanity they deserve.</p>',
    },
    'culturecontent.value_3_num': { label: 'Value 3 Number', type: 'richtext', defaultValue: '<p>03</p>' },
    'culturecontent.value_3_title': {
      label: 'Value 3 Title',
      type: 'richtext',
      defaultValue: '<p>Equity &amp; Diversity</p>',
    },
    'culturecontent.value_3_tagline': {
      label: 'Value 3 Tagline',
      type: 'richtext',
      defaultValue: '<p>Different perspectives make us stronger.</p>',
    },
    'culturecontent.value_3_body': {
      label: 'Value 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>We believe everyone should have the opportunity to contribute, grow, and succeed. We embrace different backgrounds, experiences, ideas, and perspectives because our differences make our teams—and our company—better.</p>',
    },
    'culturecontent.value_4_num': { label: 'Value 4 Number', type: 'richtext', defaultValue: '<p>04</p>' },
    'culturecontent.value_4_title': { label: 'Value 4 Title', type: 'richtext', defaultValue: '<p>Unity</p>' },
    'culturecontent.value_4_tagline': {
      label: 'Value 4 Tagline',
      type: 'richtext',
      defaultValue: '<p>Better together.</p>',
    },
    'culturecontent.value_4_body': {
      label: 'Value 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Great things happen when people work together. We build strong relationships, communicate openly, support one another, and work as one team toward a shared goal.</p>',
    },
    'culturecontent.value_5_num': { label: 'Value 5 Number', type: 'richtext', defaultValue: '<p>05</p>' },
    'culturecontent.value_5_title': { label: 'Value 5 Title', type: 'richtext', defaultValue: '<p>Excellence</p>' },
    'culturecontent.value_5_tagline': {
      label: 'Value 5 Tagline',
      type: 'richtext',
      defaultValue: '<p>Always find a better way.</p>',
    },
    'culturecontent.value_5_body': {
      label: 'Value 5 Body',
      type: 'richtext',
      defaultValue:
        "<p>We don't settle for good enough. We challenge ourselves to raise the bar, solve problems creatively, and create opportunities for our people and our communities to thrive.</p>",
    },
    'culturecontent.value_6_num': { label: 'Value 6 Number', type: 'richtext', defaultValue: '<p>06</p>' },
    'culturecontent.value_6_title': {
      label: 'Value 6 Title',
      type: 'richtext',
      defaultValue: '<p>Responsibility</p>',
    },
    'culturecontent.value_6_tagline': {
      label: 'Value 6 Tagline',
      type: 'richtext',
      defaultValue: '<p>Own it. Show up. Make an impact.</p>',
    },
    'culturecontent.value_6_body': {
      label: 'Value 6 Body',
      type: 'richtext',
      defaultValue:
        '<p>We take responsibility for our work and our commitments. We strive to be responsive to our residents, supportive of our team members, and good neighbors in the communities we serve.</p>',
    },
    'culturecontent.value_7_num': { label: 'Value 7 Number', type: 'richtext', defaultValue: '<p>07</p>' },
    'culturecontent.value_7_title': {
      label: 'Value 7 Title',
      type: 'richtext',
      defaultValue: '<p>Environmental Stewardship</p>',
    },
    'culturecontent.value_7_tagline': {
      label: 'Value 7 Tagline',
      type: 'richtext',
      defaultValue: '<p>Leave things better than we found them.</p>',
    },
    'culturecontent.value_7_body': {
      label: 'Value 7 Body',
      type: 'richtext',
      defaultValue:
        '<p>We have a responsibility to the places we call home. We work with our teams, partners, residents, and neighbors to create cleaner, healthier communities and embrace more sustainable ways of doing business.</p>',
    },
    'culturecontent.quotes_eyebrow': {
      label: 'Quotes Eyebrow',
      type: 'richtext',
      defaultValue: '<p>our people</p>',
    },
    'culturecontent.quotes_headline': {
      label: 'Quotes Headline',
      type: 'richtext',
      defaultValue: '<p>Hear from our team members</p>',
    },
    'culturecontent.quote_1': {
      label: 'Quote 1',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark genuinely invests in its people. Through mentorship and real hands-on responsibility, I grew from an onsite role into regional leadership faster than I imagined possible.</p>',
    },
    'culturecontent.quote_2': {
      label: 'Quote 2',
      type: 'richtext',
      defaultValue:
        '<p>The culture here is collaborative, not competitive. Teams across markets share what works, and leadership actually listens. It makes every community we touch better.</p>',
    },
    'culturecontent.quote_3': {
      label: 'Quote 3',
      type: 'richtext',
      defaultValue:
        '<p>I started in the internship program with no multifamily experience. Within two years I was running my own community. The pathway here is real if you put in the work.</p>',
    },
    'culturecontent.journey_headline': {
      label: 'Journey Headline',
      type: 'richtext',
      defaultValue: '<p>Our journey: from vision to growth</p>',
    },
    'culturecontent.journey_1_year': { label: 'Journey 1 Year', type: 'richtext', defaultValue: '<p>2016</p>' },
    'culturecontent.journey_1_title': {
      label: 'Journey 1 Title',
      type: 'richtext',
      defaultValue: '<p>A Strong Foundation</p>',
    },
    'culturecontent.journey_1_body': {
      label: 'Journey 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>Our leadership team entered 2016 with decades of multifamily experience and a proven history of transforming communities through thoughtful investment, operational excellence, and resident-focused management.</p>',
    },
    'culturecontent.journey_2_year': { label: 'Journey 2 Year', type: 'richtext', defaultValue: '<p>2018</p>' },
    'culturecontent.journey_2_title': {
      label: 'Journey 2 Title',
      type: 'richtext',
      defaultValue: '<p>Purposeful Growth</p>',
    },
    'culturecontent.journey_2_body': {
      label: 'Journey 2 Body',
      type: 'richtext',
      defaultValue:
        "<p>Rather than pursuing growth for growth's sake, we've strategically expanded throughout the Sunbelt, identifying markets with long-term opportunity and communities with untapped potential.</p>",
    },
    'culturecontent.journey_3_year': { label: 'Journey 3 Year', type: 'richtext', defaultValue: '<p>2020</p>' },
    'culturecontent.journey_3_title': {
      label: 'Journey 3 Title',
      type: 'richtext',
      defaultValue: '<p>Art Belongs Everywhere</p>',
    },
    'culturecontent.journey_3_body': {
      label: 'Journey 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark launches Artists in Residence, bringing creativity into everyday living through housing, studio space, and on-site programming for residents.</p>',
    },
    'culturecontent.journey_4_year': { label: 'Journey 4 Year', type: 'richtext', defaultValue: '<p>2022</p>' },
    'culturecontent.journey_4_title': {
      label: 'Journey 4 Title',
      type: 'richtext',
      defaultValue: '<p>Growing Across the Sunbelt</p>',
    },
    'culturecontent.journey_4_body': {
      label: 'Journey 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>The portfolio continues to expand across high-growth Sunbelt markets, pairing operational discipline with a culture that puts people first.</p>',
    },
    'culturecontent.journey_5_year': { label: 'Journey 5 Year', type: 'richtext', defaultValue: '<p>2024</p>' },
    'culturecontent.journey_5_title': {
      label: 'Journey 5 Title',
      type: 'richtext',
      defaultValue: '<p>Creating Places People Love</p>',
    },
    'culturecontent.journey_5_body': {
      label: 'Journey 5 Body',
      type: 'richtext',
      defaultValue:
        '<p>Every renovation, amenity upgrade, and resident experience is designed with one goal—communities where people genuinely enjoy living, while delivering long-term value for partners.</p>',
    },
    'culturecontent.impact_banner': {
      label: 'Impact Banner',
      type: 'richtext',
      defaultValue: '<p>COMMUNITY IMPACT</p>',
    },
    'culturecontent.stat_1_value': { label: 'Stat 1 Value', type: 'richtext', defaultValue: '<p>214+</p>' },
    'culturecontent.stat_1_label': {
      label: 'Stat 1 Label',
      type: 'richtext',
      defaultValue: '<p>Hot meals served</p>',
    },
    'culturecontent.stat_2_value': { label: 'Stat 2 Value', type: 'richtext', defaultValue: '<p>12,744</p>' },
    'culturecontent.stat_2_label': {
      label: 'Stat 2 Label',
      type: 'richtext',
      defaultValue: '<p>Meals packed</p>',
    },
    'culturecontent.stat_3_value': { label: 'Stat 3 Value', type: 'richtext', defaultValue: '<p>3,263</p>' },
    'culturecontent.stat_3_label': {
      label: 'Stat 3 Label',
      type: 'richtext',
      defaultValue: '<p>Meals provided</p>',
    },
    'culturecontent.stat_4_value': { label: 'Stat 4 Value', type: 'richtext', defaultValue: '<p>34</p>' },
    'culturecontent.stat_4_label': {
      label: 'Stat 4 Label',
      type: 'richtext',
      defaultValue: '<p>Educators housed in 2024</p>',
    },
    'culturecontent.impact_intro': {
      label: 'Impact Intro',
      type: 'richtext',
      defaultValue:
        '<p>Our commitment to community goes beyond the walls of our properties. American Landmark partners with local organizations, nonprofits, educators, and community leaders to create opportunities, provide resources, and help strengthen the neighborhoods where we live and work.</p>',
    },
    'culturecontent.impact_nav_label': {
      label: 'Impact Nav Label',
      type: 'richtext',
      defaultValue: '<p>Our community impact</p>',
    },
    'culturecontent.giving_1_eyebrow': {
      label: 'Giving 1 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Making an Impact Together</p>',
    },
    'culturecontent.giving_1_title': {
      label: 'Giving 1 Title',
      type: 'richtext',
      defaultValue: '<p>Giving Back Efforts</p>',
    },
    'culturecontent.giving_1_points': {
      label: 'Giving 1 Points',
      type: 'richtext',
      defaultValue:
        '<ul><li>Helped serve 214 hot meals at the Trinity Cafe with @feedingtampabay</li><li>Wrapped 19,618 diapers to cover 392 babies with @nc_diaperbank</li><li>Packed 59 boxes, which makes 12,744 meals with @fmsc_org</li><li>Atlanta team volunteered at @acfb to provide 3,263 meals from 3,916 pounds of food</li></ul>',
    },
    'culturecontent.giving_2_eyebrow': {
      label: 'Giving 2 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Creating Opportunity</p>',
    },
    'culturecontent.giving_2_title': {
      label: 'Giving 2 Title',
      type: 'richtext',
      defaultValue: '<p>City Teaching Alliance</p>',
    },
    'culturecontent.giving_2_points': {
      label: 'Giving 2 Points',
      type: 'richtext',
      defaultValue:
        '<ul><li>Non-profit dedicated to identifying, preparing, and retaining exceptional career educators.</li><li>Partnership ensures early-career teachers have access to high-quality housing, letting them focus on improving educational and life outcomes.</li><li>Participants in the residency program receive a one- or two-bedroom apartment at a reduced monthly rental rate for a one-year lease term.</li><li>Housed 34 educators in 2024.</li></ul>',
    },
    'culturecontent.giving_3_eyebrow': {
      label: 'Giving 3 Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Investing in the Future</p>',
    },
    'culturecontent.giving_3_title': {
      label: 'Giving 3 Title',
      type: 'richtext',
      defaultValue: '<p>Brooks DeBartolo Collegiate High School</p>',
    },
    'culturecontent.giving_3_points': {
      label: 'Giving 3 Points',
      type: 'richtext',
      defaultValue:
        '<ul><li>For four years, American Landmark has partnered with Brooks DeBartolo Collegiate High School to help prepare students for life and future careers.</li><li>Together, we provide opportunities to develop practical skills including résumé writing, interviewing, and personal financial literacy.</li><li>2 interns welcomed to the American Landmark team in 2024.</li></ul>',
    },
    'culturecontent.air_eyebrow': {
      label: 'AIR Eyebrow',
      type: 'richtext',
      defaultValue: '<p>Artists in Residence</p>',
    },
    'culturecontent.air_headline': {
      label: 'AIR Headline',
      type: 'richtext',
      defaultValue: '<p>Art Belongs in Everyday Life.</p>',
    },
    'culturecontent.air_body_1': {
      label: 'AIR Body 1',
      type: 'richtext',
      defaultValue:
        '<p>In 2020, American Landmark launched Artists in Residence with a simple idea: make art more accessible by bringing it directly into the places people call home.</p>',
    },
    'culturecontent.air_body_2': {
      label: 'AIR Body 2',
      type: 'richtext',
      defaultValue:
        '<p>The program provides select visual artists, culinary artists, musicians, and educators with no-cost housing, a monthly teaching stipend, and dedicated studio space for up to one year. In return, artists share their talents through weekly, on-site programming that inspires creativity, connection, and community.</p>',
    },
    'culturecontent.air_body_3': {
      label: 'AIR Body 3',
      type: 'richtext',
      defaultValue:
        '<p>What began as a bold idea has become a growing platform for emerging creatives—and a new way for neighbors to come together.</p>',
    },
    'culturecontent.air_t1_label': { label: 'AIR Timeline 1 Label', type: 'richtext', defaultValue: '<p>2020</p>' },
    'culturecontent.air_t1_title': {
      label: 'AIR Timeline 1 Title',
      type: 'richtext',
      defaultValue: '<p>A New Idea Takes Shape</p>',
    },
    'culturecontent.air_t1_body': {
      label: 'AIR Timeline 1 Body',
      type: 'richtext',
      defaultValue:
        '<p>American Landmark launches the Artists in Residence program, creating a new model for bringing art, creativity, and community into everyday living.</p>',
    },
    'culturecontent.air_t2_label': {
      label: 'AIR Timeline 2 Label',
      type: 'richtext',
      defaultValue: '<p>The First Residents</p>',
    },
    'culturecontent.air_t2_title': {
      label: 'AIR Timeline 2 Title',
      type: 'richtext',
      defaultValue: '<p>Creating Space for Creativity</p>',
    },
    'culturecontent.air_t2_body': {
      label: 'AIR Timeline 2 Body',
      type: 'richtext',
      defaultValue:
        '<p>The program begins welcoming select artists into American Landmark communities, providing housing, studio space, and the opportunity to share their creative talents with residents.</p>',
    },
    'culturecontent.air_t3_label': {
      label: 'AIR Timeline 3 Label',
      type: 'richtext',
      defaultValue: '<p>Growing the Program</p>',
    },
    'culturecontent.air_t3_title': {
      label: 'AIR Timeline 3 Title',
      type: 'richtext',
      defaultValue: '<p>More Artists. More Communities.</p>',
    },
    'culturecontent.air_t3_body': {
      label: 'AIR Timeline 3 Body',
      type: 'richtext',
      defaultValue:
        '<p>Artists in Residence expands beyond its beginnings, supporting emerging creatives across multiple states and creating meaningful opportunities for artists to develop their careers.</p>',
    },
    'culturecontent.air_t4_label': {
      label: 'AIR Timeline 4 Label',
      type: 'richtext',
      defaultValue: '<p>Community in Action</p>',
    },
    'culturecontent.air_t4_title': {
      label: 'AIR Timeline 4 Title',
      type: 'richtext',
      defaultValue: '<p>Art Brings People Together</p>',
    },
    'culturecontent.air_t4_body': {
      label: 'AIR Timeline 4 Body',
      type: 'richtext',
      defaultValue:
        '<p>Weekly programming transforms apartment communities into places for creativity and connection. Residents can learn, participate, discover new talents, and experience art right where they live.</p>',
    },
    'culturecontent.air_t5_label': { label: 'AIR Timeline 5 Label', type: 'richtext', defaultValue: '<p>Today</p>' },
    'culturecontent.air_t5_title': {
      label: 'AIR Timeline 5 Title',
      type: 'richtext',
      defaultValue: '<p>A Platform for Creative Talent</p>',
    },
    'culturecontent.air_t5_body': {
      label: 'AIR Timeline 5 Body',
      type: 'richtext',
      defaultValue:
        '<p>Artists in Residence continues to support visual artists, culinary artists, musicians, and educators while creating memorable experiences for American Landmark residents.</p>',
    },
    'culturecontent.cta_eyebrow': { label: 'CTA Eyebrow', type: 'richtext', defaultValue: '<p>careers</p>' },
    'culturecontent.cta_headline': {
      label: 'CTA Headline',
      type: 'richtext',
      defaultValue: '<p>Ready to build something meaningful with us?</p>',
    },
    'culturecontent.cta_body': {
      label: 'CTA Body',
      type: 'richtext',
      defaultValue:
        "<p>We're always looking for people who care deeply, work boldly, and want to grow alongside a team that has each other's backs. Explore open roles and find your next chapter at American Landmark.</p>",
    },
  },
  slotGroups: {
    'culturecontent.about_intro_cta': aboutIntroCtaDef,
    'culturecontent.careers_intro_cta': careersIntroCtaDef,
    'culturecontent.about_journey_cta': aboutJourneyCtaDef,
    'culturecontent.careers_journey_cta': careersJourneyCtaDef,
    'culturecontent.careers_cta': careersCtaDef,
    'culturecontent.about_cta': aboutCtaDef,
    'culturecontent.impact_hero': impactHeroDef,
    'culturecontent.ethos_img_1': ethosImg1Def,
    'culturecontent.ethos_img_2': ethosImg2Def,
    'culturecontent.ethos_img_3': ethosImg3Def,
    'culturecontent.ethos_img_4': ethosImg4Def,
    'culturecontent.ethos_img_5': ethosImg5Def,
    'culturecontent.ethos_img_6': ethosImg6Def,
    'culturecontent.ethos_img_7': ethosImg7Def,
    'culturecontent.journey_img_1': journeyImg1Def,
    'culturecontent.journey_img_2': journeyImg2Def,
    'culturecontent.journey_img_3': journeyImg3Def,
    'culturecontent.journey_img_4': journeyImg4Def,
    'culturecontent.journey_img_5': journeyImg5Def,
    'culturecontent.giving_img_1': givingImg1Def,
    'culturecontent.giving_img_2': givingImg2Def,
    'culturecontent.giving_img_3': givingImg3Def,
    'culturecontent.marquee_img_1': marqueeImg1Def,
    'culturecontent.marquee_img_2': marqueeImg2Def,
    'culturecontent.marquee_img_3': marqueeImg3Def,
    'culturecontent.marquee_img_4': marqueeImg4Def,
    'culturecontent.marquee_img_5': marqueeImg5Def,
    'culturecontent.marquee_img_6': marqueeImg6Def,
    'culturecontent.marquee_img_7': marqueeImg7Def,
    'culturecontent.marquee_img_8': marqueeImg8Def,
    'culturecontent.marquee_img_9': marqueeImg9Def,
    'culturecontent.marquee_img_10': marqueeImg10Def,
  },
};

(CultureContent as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CultureContent;
